import { FC, useEffect, useContext, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { format } from "date-fns";

import { GeographicalStatisticsObject, StatisticsApiResponse } from "./types";

import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { ToastContext } from "src/context/toastContext";
import { LoaderContext } from "src/context/loaderContext";
import { ToastTypes } from "src/types";

import FooterText from "src/components/footerText";
import CaseVolume from "src/components/manufacturer/caseVolumeLineChart";
import GeographicalStatistics from "src/components/manufacturer/geographicalStatistics";
import CaseFilter from "src/components/manufacturer/statistics";
import { StatisticsSearchProps } from "src/components/manufacturer/statistics/types";
import { AuthContext } from "../../context/authContext";
import AuthMaunfacturerSubHeader from "src/components/manufacturer/authManufacturerSubHeader";
import CustomComponentForTransfer from "src/components/manufacturer/customComponentForTransfer";
import CustomComponentForPriorAuth from "src/components/manufacturer/customComponentCasesByDrugsBarChart";
import CustomComponentAreaCaseBySubmission from "src/components/manufacturer/customComponentAreaCaseBySubmission";
import CustomPieChart from "src/components/manufacturer/customPieChart";
import { areManFiltersUpdated, getDefaultManFilters } from "src/helpers/manufacturer";


const Manufacturer: FC = (): JSX.Element => {
  const { showToast } = useContext(ToastContext);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const { userData } = useContext(AuthContext);
  const [searchParams, updateSearchParams] = useSearchParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const insuranceName: string | null = params.get('insurance_name');

  const [queryParams, changeQueryParams] = useState(getDefaultManFilters(searchParams, userData, "index"));

  const { data: statisticsData, mutate: getStatsData } =
    useFetch<StatisticsApiResponse>(APIEndpoints.MANUFACTURE_STATISTICS, queryParams);


  useEffect(() => {
    if (statisticsData) {
      hideLoader();
      if (statisticsData.code === 200) {
        showToast([statisticsData.message], ToastTypes.SUCCESS);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statisticsData]);


  useEffect(() => {
    if (insuranceName) {
      const updatedQueries = {
        ...queryParams,
        insurance_name: insuranceName

      }
      changeQueryParams(updatedQueries)
      getStatsData({}, {}, updatedQueries);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceName]);
  const updateQueryParamsAndCallAPI = (
    updatedQueries: StatisticsSearchProps,
    isReset: boolean = false
  ): void => {
    if (isReset) {
      updateSearchParams({});
      changeQueryParams({
        start_date: format(
          new Date().setDate(new Date().getDate() - 30),
          "yyyy-MM-dd"
        ),
        end_date: format(
          new Date().setDate(new Date().getDate()),
          "yyyy-MM-dd"
        ),
        period: "day",
        filter_type: "",
        type: "index",
        role: userData ? userData.ref_type : "",
        // soc_value: "",
        // drug_id: "",
        user_id: searchParams.get("id") || (userData ? userData.id.toString() : ""),
        // insurance_name: "",
      });
    } else {
      updateSearchParams({
        start_date: updatedQueries.start_date,
        end_date: updatedQueries.end_date,
        period: updatedQueries.period,
        filter_type: updatedQueries.filter_type
      });
      if (areManFiltersUpdated(queryParams, searchParams, userData, "index"))
        changeQueryParams(updatedQueries);
    }

    setLoader();
    getStatsData({}, {}, updatedQueries);
  };

  const getStatisticsData = () => {
    const updatedQueries = {
      ...queryParams,

    }
    changeQueryParams(updatedQueries)
    getStatsData({}, {}, updatedQueries);
  }

  return (
      <div className={"container-fluid overflow-y-auto"}>
        <div className="container ">
          <AuthMaunfacturerSubHeader/>
          {statisticsData && (
              <CaseFilter
                  defaultQueryParams={queryParams}
                  updateQueryParams={updateQueryParamsAndCallAPI}
              />
          )}
          {statisticsData && (
              <CaseVolume StatisticsObjectType={statisticsData.data} getStatisticsData={getStatisticsData}
                          buttonShows={true}/>
          )}
          {statisticsData && (
              <CustomComponentForPriorAuth StatisticsObjectType={statisticsData.data}/>
          )}
          {statisticsData && (
              <CustomPieChart pieChartObject={statisticsData.data.top_payors} title="Top 10 Payors"/>
          )}

          {(statisticsData &&
              <CustomComponentForTransfer StatisticsObjectType={statisticsData.data}/>
          )}

          {statisticsData && (
            <CustomComponentAreaCaseBySubmission 
              StatisticsObjectType={statisticsData.data} title={'Area Managers'}
              queryParams={queryParams}
              isFilterObjUpdated={() => areManFiltersUpdated(queryParams, searchParams, userData, "index")}
            />
          )}

          {statisticsData?.data && (
              <GeographicalStatistics
                  GeographicalStatisticsListType={
                    statisticsData?.data
                        .geographical_stats as unknown as GeographicalStatisticsObject[]
                  }
              />
          )}
          <FooterText/>
        </div>
      </div>
  );
};

export default Manufacturer;
